import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ExternalLink } from "lucide-react";
import {
  api,
  collectComments,
  type Comment,
  type Sentiment,
  type GeneralTag,
} from "../lib/api";
import {
  FacetBars,
  GENERAL_TAG_LABELS,
  SENTIMENT_LABELS,
  SAFETY_TAG_LABELS,
} from "./FacetBars";

/**
 * Phase 6d.2 → Phase 8 #9.5 — per-video comment intelligence, v8 multi-facet.
 *
 * The v7 one-way category→theme drill is replaced by three INDEPENDENT,
 * AND-composable facets (sentiment / general tags / topics), each a
 * click-to-filter bar. Selecting across facets narrows the inline comments
 * list; a channel-wide link carries the same composed filter to the dashboard.
 *
 * State is local (not URL) — the per-video card is a focused drill, while the
 * channel-wide dashboard owns the shareable URL contract.
 */

interface Props {
  videoId: string;
}

interface FacetState {
  sentiment: string | null;
  generalTag: string | null;
  topicTag: string | null;
}

const EMPTY_FACETS: FacetState = {
  sentiment: null,
  generalTag: null,
  topicTag: null,
};

export function VideoCommentIntelligence({ videoId }: Props) {
  const [facets, setFacets] = useState<FacetState>(EMPTY_FACETS);

  const stats = useQuery({
    queryKey: ["commentStats", videoId],
    queryFn: () => api.commentStats(videoId),
  });

  // Toggle a single facet (second click on the same value clears it).
  const toggle = (key: keyof FacetState, value: string) =>
    setFacets((cur) => ({ ...cur, [key]: cur[key] === value ? null : value }));

  if (stats.isLoading) return <Skeleton />;

  if (stats.error || !stats.data) {
    return (
      <Card>
        <CardHeader />
        <div className="text-sm text-terminal-ink-muted">
          Couldn't load comment intelligence for this video.
        </div>
      </Card>
    );
  }

  const { total, bySentiment, byGeneralTag, byTopicTag, topicLabels } =
    stats.data;

  const classifiedTotal = sumCounts(bySentiment);
  const hasTopics = Object.keys(byTopicTag).length > 0;
  const anyFacet = !!(facets.sentiment || facets.generalTag || facets.topicTag);

  if (classifiedTotal === 0) {
    return (
      <Card>
        <CardHeader />
        <div className="text-sm text-terminal-ink-muted">
          The AI hasn't tagged this video's comments yet. It works through them
          over a day or two, so check back soon. We've collected{" "}
          {total.toLocaleString("en-GB")} comment
          {total === 1 ? "" : "s"} for this video so far.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader />

      <div className="space-y-5">
        <Facet title="Sentiment">
          <FacetBars
            counts={bySentiment}
            labelMap={SENTIMENT_LABELS}
            variant="sentiment"
            selected={facets.sentiment}
            onSelect={(s) => toggle("sentiment", s)}
          />
        </Facet>

        <Facet title="What the audience is doing">
          <FacetBars
            counts={byGeneralTag}
            labelMap={GENERAL_TAG_LABELS}
            selected={facets.generalTag}
            onSelect={(g) => toggle("generalTag", g)}
            emptyMessage="No general tags on this video yet."
          />
        </Facet>

        {hasTopics && (
          <Facet title="Topics">
            <FacetBars
              counts={byTopicTag}
              labelMap={topicLabels}
              selected={facets.topicTag}
              onSelect={(t) => toggle("topicTag", t)}
            />
          </Facet>
        )}
      </div>

      {anyFacet && (
        <MatchingComments videoId={videoId} facets={facets} />
      )}

      <div className="mt-4 border-t border-white/10 pt-3 text-xs text-terminal-ink-muted">
        Across {classifiedTotal.toLocaleString("en-GB")} tagged comments on this
        video.
        {anyFacet && (
          <>
            {" "}
            <Link
              to={`/youtube/comments?${channelWideParams(facets)}`}
              className="text-[#aab2ff] hover:underline"
            >
              See these channel-wide →
            </Link>
          </>
        )}
      </div>
    </Card>
  );
}

/** Build the dashboard URL params for the current facet selection (no videoId
 *  — "channel-wide" deliberately drops the per-video scope). */
function channelWideParams(facets: FacetState): string {
  const qs = new URLSearchParams();
  if (facets.sentiment) qs.set("sentiment", facets.sentiment);
  if (facets.generalTag) qs.set("gtag", facets.generalTag);
  if (facets.topicTag) qs.set("topic", facets.topicTag);
  return qs.toString();
}

function sumCounts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

function Facet({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {title}
      </div>
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Matching comments (inline, AND-composed across the selected facets)        */
/* -------------------------------------------------------------------------- */

const PAGE_SIZE = 25;

function MatchingComments({
  videoId,
  facets,
}: {
  videoId: string;
  facets: FacetState;
}) {
  const [pages, setPages] = useState<
    Array<{ items: Comment[]; cont: string | null }>
  >([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const key = [
    "videoCommentsList",
    videoId,
    facets.sentiment ?? "",
    facets.generalTag ?? "",
    facets.topicTag ?? "",
  ];

  // Facet values originate from the stats keys, so they're valid enum members.
  const params = {
    videoId,
    sentiment: (facets.sentiment ?? undefined) as Sentiment | undefined,
    generalTag: (facets.generalTag ?? undefined) as GeneralTag | undefined,
    topicTag: facets.topicTag ?? undefined,
  };

  // collectComments follows the server's bounded-scan continuations so a
  // screenful of matches comes back in one go, even for a sparse facet combo.
  const initial = useQuery({
    queryKey: key,
    queryFn: () => collectComments(params, PAGE_SIZE),
  });

  const items =
    pages.length > 0
      ? pages.flatMap((p) => p.items)
      : (initial.data?.items ?? []);
  const continuation =
    pages.length > 0
      ? pages[pages.length - 1].cont
      : (initial.data?.continuation ?? null);

  const loadMore = async () => {
    if (!continuation || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await collectComments(params, PAGE_SIZE, continuation);
      setPages((cur) => [
        ...(cur.length === 0
          ? [{ items: initial.data!.items, cont: initial.data!.continuation }]
          : cur),
        { items: next.items, cont: next.continuation },
      ]);
    } finally {
      setLoadingMore(false);
    }
  };

  if (initial.isLoading) {
    return (
      <div className="mt-4 space-y-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-lg bg-white/5" />
        ))}
      </div>
    );
  }

  if (initial.error) {
    return (
      <div className="mt-4 text-sm text-terminal-ink-muted">
        Couldn't load matching comments.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="mt-5 border-t border-white/10 pt-4 text-sm text-terminal-ink-muted">
        No comments match this filter.
      </div>
    );
  }

  return (
    <div className="mt-5 border-t border-white/10 pt-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-terminal-ink-muted">
        Matching comments
      </div>
      <ul className="space-y-2">
        {items.map((c) => (
          <CommentRow key={c.commentThreadId} comment={c} />
        ))}
      </ul>
      {continuation && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          className="mt-2 rounded border border-white/10 bg-terminal-panel px-3 py-1 text-xs font-semibold text-terminal-ink-muted transition-colors hover:text-terminal-ink disabled:opacity-50"
        >
          {loadingMore ? "Loading…" : `Load ${PAGE_SIZE} more`}
        </button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Comment row (used here and by the channel-wide dashboard) — v8 badges      */
/* -------------------------------------------------------------------------- */

// Literal class strings (Tailwind JIT can't see dynamically-built names).
// Dark-terminal pills: sentiment uses the markets up/down hues (the muted
// light-shell sentiment-* tokens don't read on bg-terminal-panel).
const SENTIMENT_PILL: Record<string, string> = {
  supportive: "bg-up/15 text-up",
  unsupportive: "bg-down/15 text-down",
  neutral: "bg-white/8 text-terminal-ink-muted",
};

const SAFETY_PILL: Record<string, string> = {
  threat_or_abuse: "bg-down/15 text-down",
  spam_bot: "bg-white/8 text-terminal-ink-muted",
};

export function CommentRow({ comment }: { comment: Comment }) {
  const youtubeUrl = `https://www.youtube.com/watch?v=${comment.videoId}&lc=${comment.commentThreadId}`;
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Detect overflow by comparing scrollHeight to clientHeight while the
  // text element is still in its clamped state. Re-run on resize because the
  // line count depends on the column width.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      if (expanded) return;
      setOverflows(el.scrollHeight - el.clientHeight > 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [comment.text, expanded]);

  const hasSafety = comment.safetyTags.length > 0;

  return (
    <li className="rounded-lg border border-white/10 bg-terminal-panel p-3 text-sm">
      {/* Only present for safety reviewers on flagged rows (GDPR #27/#29). */}
      {comment.author && (
        <div className="mb-1 flex flex-wrap items-baseline gap-2 text-xs">
          {comment.authorChannelId ? (
            <a
              href={`https://www.youtube.com/channel/${comment.authorChannelId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#aab2ff] hover:underline"
            >
              {comment.author}
            </a>
          ) : (
            <span className="font-medium text-terminal-ink">{comment.author}</span>
          )}
          <span className="text-terminal-ink-muted">visible to safety reviewers</span>
        </div>
      )}
      <div
        ref={textRef}
        className={`whitespace-pre-wrap text-terminal-ink ${expanded ? "" : "line-clamp-3"}`}
      >
        {comment.text}
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs font-medium text-[#aab2ff] hover:underline"
          aria-expanded={expanded}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}

      {/* v8 badge row: sentiment (colored) · general tags · topics · safety */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        {comment.sentiment && (
          <span
            className={`rounded px-1.5 py-0.5 font-medium ${
              SENTIMENT_PILL[comment.sentiment] ?? "bg-white/8 text-terminal-ink-muted"
            }`}
          >
            {SENTIMENT_LABELS[comment.sentiment] ?? comment.sentiment}
          </span>
        )}
        {comment.generalTags.map((g) => (
          <span key={g} className="rounded bg-white/8 px-1.5 py-0.5 text-terminal-ink-muted">
            {GENERAL_TAG_LABELS[g] ?? g}
          </span>
        ))}
        {comment.topicLabels.map((t) => (
          <span
            key={t.id}
            className="rounded bg-agenda-public/15 px-1.5 py-0.5 text-[#8ab4ff]"
            title={t.id}
          >
            {t.label}
          </span>
        ))}
        {comment.safetyTags.map((s) => (
          <span
            key={s}
            className={`rounded px-1.5 py-0.5 font-medium ${
              SAFETY_PILL[s] ?? "bg-white/8 text-terminal-ink-muted"
            }`}
          >
            ⚑ {SAFETY_TAG_LABELS[s] ?? s}
          </span>
        ))}
      </div>

      {/* safety_notes surfaced on expand when the row is flagged. */}
      {expanded && hasSafety && comment.safetyNotes && (
        <div className="mt-2 rounded border-l-2 border-down/40 bg-down/10 px-2 py-1 text-xs text-terminal-ink-muted">
          <span className="font-medium text-terminal-ink">Safety note:</span>{" "}
          {comment.safetyNotes}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-terminal-ink-muted">
        {comment.likeCount > 0 && (
          <span className="font-mono tabular-nums">
            {comment.likeCount.toLocaleString("en-GB")} ♥
          </span>
        )}
        <a
          href={youtubeUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open on YouTube"
          title="Open on YouTube"
          className="ml-auto text-terminal-ink-muted transition-colors hover:text-terminal-ink"
        >
          <ExternalLink size={13} />
        </a>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout primitives                                                          */
/* -------------------------------------------------------------------------- */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="mt-8 rounded-xl border border-white/10 bg-terminal p-4 sm:p-5">
      {children}
    </section>
  );
}

function CardHeader() {
  return (
    <div className="mb-4 flex items-center gap-2 text-sm font-bold tracking-tight text-terminal-ink">
      <span>Comment intelligence</span>
      <ChevronRight size={14} className="text-terminal-ink-muted/60" />
      <span className="text-xs font-normal text-terminal-ink-muted">
        click a sentiment, tag or topic to filter
      </span>
    </div>
  );
}

function Skeleton() {
  return (
    <section className="mt-8 h-64 animate-pulse rounded-xl bg-terminal-panel/50" />
  );
}
